import { useState, useEffect } from 'react';

interface ITableState {
    editRowIndex: number;
    editColumnIndex: number;
    tempEditColumnIndex: number;
    tableId: string;
}

class TableStateStore {
    private tableStates: Record<string, ITableState> = {};
    private listeners: Map<string, Set<() => void>> = new Map();
    private activeEditTableId: string | null = null;

    getState(tableId: string): ITableState {
        return this.tableStates[tableId] || {
            editRowIndex: -1,
            editColumnIndex: -1,
            tempEditColumnIndex: -1,
            tableId
        };
    }

    updateState(tableId: string, updates: Partial<ITableState>) {
        const currentState = this.getState(tableId);

        let hasChanges = false;
        for (const key in updates) {
            if (updates[key as keyof ITableState] !== currentState[key as keyof ITableState]) {
                hasChanges = true;
                break;
            }
        }

        if (!hasChanges) {
            return;
        }

        const isEnteringEditMode =
            (updates.editRowIndex !== undefined && updates.editRowIndex !== -1) ||
            (updates.editColumnIndex !== undefined && updates.editColumnIndex !== -1);

        this.tableStates[tableId] = {
            ...currentState,
            ...updates
        };

        if (isEnteringEditMode) {
            const previousActiveTableId = this.activeEditTableId;
            this.activeEditTableId = tableId;

            if (previousActiveTableId && previousActiveTableId !== tableId) {
                const previousTableState = this.tableStates[previousActiveTableId];
                if (previousTableState) {
                    this.tableStates[previousActiveTableId] = {
                        ...previousTableState,
                        editRowIndex: -1,
                        editColumnIndex: -1,
                        tempEditColumnIndex: -1
                    };
                    this.notifyListeners(previousActiveTableId);
                }
            }
        }
        else if (this.activeEditTableId === tableId &&
            updates.editRowIndex === -1 &&
            updates.editColumnIndex === -1) {
            this.activeEditTableId = null;
        }

        this.notifyListeners(tableId);
    }

    private notifyListeners(tableId: string) {
        const tableListeners = this.listeners.get(tableId);
        if (tableListeners) {
            tableListeners.forEach(listener => listener());
        }
    }

    subscribe(tableId: string, listener: () => void) {
        if (!this.listeners.has(tableId)) {
            this.listeners.set(tableId, new Set());
        }

        const tableListeners = this.listeners.get(tableId)!;
        tableListeners.add(listener);

        return () => {
            tableListeners.delete(listener);
            if (tableListeners.size === 0) {
                this.listeners.delete(tableId);
            }
        };
    }

    removeTable(tableId: string) {
        delete this.tableStates[tableId];
        this.listeners.delete(tableId);

        if (this.activeEditTableId === tableId) {
            this.activeEditTableId = null;
        }
    }
}

const store = new TableStateStore();

export function useTableState(tableId: string) {
    const [state, setState] = useState<ITableState>(() => store.getState(tableId));

    useEffect(() => {
        const unsubscribe = store.subscribe(tableId, () => {
            setState(store.getState(tableId));
        });

        return () => {
            unsubscribe();
            store.removeTable(tableId);
        };
    }, [tableId]);

    const updateState = (updates: Partial<ITableState>) => {
        store.updateState(tableId, updates);
    };

    return {
        state,
        updateState
    };
}